import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { backlogApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

const PRIORITIES = ['P0', 'P1', 'P2', 'P3'];
const PRIORITY_CLS = {
  P0: 'bg-red-100 text-red-800 border-red-300',
  P1: 'bg-orange-100 text-orange-800 border-orange-300',
  P2: 'bg-amber-100 text-amber-800 border-amber-300',
  P3: 'bg-slate-100 text-slate-700 border-slate-300',
};

/**
 * Dialog riutilizzabile per creare o modificare un'idea del backlog.
 *
 * Usato sia dalla pagina /backlog (con `idea` per editare) sia dal pulsante
 * "Suggerisci miglioramento" in sidebar (sempre `idea=null`, con
 * `prefillDescription` che inserisce contesto come la pagina corrente).
 *
 * Props:
 *   open                 controllato dall'esterno
 *   idea                 oggetto idea per modifica (null = nuova)
 *   prefillDescription   testo da pre-popolare in description (solo per "nuova")
 *   canEditWorkflow      true → mostra selettore priorità (sempre vero per "nuova")
 *   onClose              callback chiusura senza salvare
 *   onSaved              callback dopo save riuscito
 */
export default function IdeaDialog({
  open, idea, prefillDescription, canEditWorkflow, onClose, onSaved,
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('P2');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTitle(idea?.title || '');
    setDescription(idea?.description || prefillDescription || '');
    setPriority(idea?.priority || 'P2');
    setErr(null);
  }, [open, idea, prefillDescription]);

  const handleSave = async () => {
    if (!title.trim()) { setErr('Il titolo è obbligatorio'); return; }
    setSaving(true); setErr(null);
    try {
      if (idea) {
        const payload = { title: title.trim(), description: description.trim() };
        if (canEditWorkflow) payload.priority = priority;
        await backlogApi.update(idea.idea_id, payload);
      } else {
        await backlogApi.create({ title: title.trim(), description: description.trim(), priority });
      }
      onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="rounded-none max-w-xl" data-testid="backlog-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{idea ? 'Modifica idea' : 'Nuova idea'}</DialogTitle>
          <DialogDescription>
            {idea
              ? 'Aggiorna titolo o descrizione. La priorità è modificabile solo dagli Operations Manager.'
              : 'Annota un\'idea per StoreOps. Operations Manager assegnerà priorità e stato.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Titolo *</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Notifica Slack quando uno store entra in rosso"
              className="rounded-none"
              data-testid="backlog-dialog-title"
              autoFocus
              maxLength={200}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Descrizione</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Contesto, motivazione, esempi d'uso..."
              className="w-full px-3 py-2 border border-slate-300 focus:border-slate-900 focus:outline-none text-sm min-h-[120px]"
              data-testid="backlog-dialog-description"
              maxLength={4000}
            />
          </div>
          {(canEditWorkflow || !idea) && (
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Priorità</label>
              <div className="flex gap-1">
                {PRIORITIES.map(p => (
                  <button
                    key={p}
                    onClick={() => setPriority(p)}
                    className={`px-3 py-1.5 text-xs font-bold border ${
                      priority === p ? PRIORITY_CLS[p] : 'bg-white text-slate-400 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`backlog-dialog-priority-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              {!canEditWorkflow && (
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Suggerisci una priorità: solo OPS può confermarla.
                </p>
              )}
            </div>
          )}
          {err && (
            <div className="px-3 py-2 border border-red-200 bg-red-50 text-xs text-red-800" data-testid="backlog-dialog-error">
              {err}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-none">
            <X className="w-4 h-4 mr-1.5" /> Annulla
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-none bg-black text-white hover:bg-slate-900" data-testid="backlog-dialog-save">
            <Check className="w-4 h-4 mr-1.5" /> {saving ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
